import React, { useState, useEffect, StrictMode, useRef } from "react";
import { createRoot } from "react-dom/client";

// CRC32 implementation for file integrity check
function crc32(data: ArrayBuffer): number {
  let crc = 0xFFFFFFFF;
  const table = getCRC32Table();
  const bytes = new Uint8Array(data);

  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ table[(crc ^ bytes[i]) & 0xFF];
  }

  return (crc ^ 0xFFFFFFFF) >>> 0;
}

function getCRC32Table(): number[] {
  const table: number[] = [];
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c;
  }
  return table;
}

interface TestResult {
  id: string;
  name: string;
  description: string;
  status: "pending" | "running" | "passed" | "failed";
  message?: string;
  details?: Record<string, unknown>;
}

interface BrowserInfo {
  browserName: string;
  browserVersion: string;
  isRecent: boolean;
}

function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  let name = "Unknown";
  let version = "";

  if (ua.includes("Firefox")) {
    name = "Firefox";
    const match = ua.match(/Firefox\/(\d+)/);
    version = match ? match[1] : "";
  } else if (ua.includes("Edg/")) {
    name = "Edge";
    const match = ua.match(/Edg\/(\d+)/);
    version = match ? match[1] : "";
  } else if (ua.includes("Chrome")) {
    name = "Chrome";
    const match = ua.match(/Chrome\/(\d+)/);
    version = match ? match[1] : "";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    name = "Safari";
    const match = ua.match(/Version\/(\d+)/);
    version = match ? match[1] : "";
  }

  const isRecent = version ? parseInt(version) >= 120 : true;

  return { browserName: name, browserVersion: version, isRecent };
}

const TESTS: Omit<TestResult, "status" | "message" | "details">[] = [
  {
    id: "browser",
    name: "Browser Version",
    description: "Check if your browser is up-to-date (less than 1 year old)",
  },
  {
    id: "connection",
    name: "Connection Test",
    description: "Verify we can reach the video file server",
  },
  {
    id: "speed",
    name: "Download Speed",
    description: "Measure how fast your connection can download data",
  },
  {
    id: "integrity",
    name: "File Integrity",
    description: "Download the full file and verify it wasn't corrupted",
  },
];

function App() {
  const [checkBrowser, setCheckBrowser] = useState(true);
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  // Default test URL - hidden from users
  const testUrl = "https://storage.googleapis.com/90seconds-production-content-v2-uploads/brands/01a940d9-f7bf-4b52-9ea3-5bad20bfe9f0/orders/ec48e040-f9dc-4de6-9a78-0990f555ae3d/services/31d0d291-672c-424c-aed9-9839a9f16358/tasks/5e942210-b7d6-47d0-bec7-3799c5997dd3/0cca0a87-9c79-4f12-8d42-0688579ea18c/01/7367e0b0_4KB-ROLLS27.mp4?t=" + Date.now();

  const updateResult = (id: string, status: TestResult["status"], message?: string, details?: Record<string, unknown>) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, status, message, details } : r));
  };

  const runTests = async () => {
    setIsRunning(true);
    setResults(TESTS.filter(t => t.id !== "browser" || checkBrowser).map(t => ({
      ...t,
      status: "running" as const,
    })));

    // Test 1: Browser version check
    if (checkBrowser) {
      await new Promise(r => setTimeout(r, 500)); // Small delay for UX
      const info = detectBrowser();
      if (info.isRecent) {
        updateResult("browser", "passed", `You're using ${info.browserName} ${info.browserVersion}, which is up-to-date`);
      } else {
        updateResult("browser", "failed", `${info.browserName} ${info.browserVersion} is more than 1 year old. Consider updating your browser for the best experience.`);
      }
    }

    // Test 2: Connection Test
    updateResult("connection", "running");
    try {
      const start = performance.now();
      const response = await fetch(testUrl, { method: "HEAD", headers: { "Cache-Control": "no-cache, no-store, must-revalidate" } });
      const time = performance.now() - start;

      if (response.ok) {
        const size = response.headers.get("content-length");
        const sizeMB = size ? (parseInt(size) / 1024 / 1024).toFixed(1) : "unknown";
        const isSlow = time > 2000;
        const speedGrade = time > 3000 ? "Very Slow" : time > 1500 ? "Slow" : time > 500 ? "Good" : "Fast";

        updateResult("connection", "passed",
          isSlow
            ? `Connection speed: ${speedGrade}. Some delays may occur when loading videos.`
            : `Connection speed: ${speedGrade}. Videos will load quickly.`,
          { speedGrade, timeMs: time.toFixed(0) }
        );
      } else {
        updateResult("connection", "failed", `Server responded with error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      const isCors = error instanceof Error && error.message.includes("Failed to fetch");
      updateResult("connection", "failed", isCors
        ? "Your browser's security settings are blocking access to our server. This is usually fixed by configuring CORS on the server side."
        : `Could not connect to server: ${error instanceof Error ? error.message : "Unknown error"}`);
    }

    // Test 3: Download Speed & Integrity (combined - download once with progress)
    updateResult("speed", "running");
    updateResult("integrity", "running");

    try {
      // First get file size
      const headRes = await fetch(testUrl, { method: "HEAD", headers: { "Cache-Control": "no-cache" } });
      const totalBytes = parseInt(headRes.headers.get("content-length") || "0");

      // Download with progress using fetch + ReadableStream
      const res = await fetch(testUrl, { headers: { "Cache-Control": "no-cache" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("ReadableStream not supported");

      // Show initial progress
      setDownloadProgress(0);

      const chunks: Uint8Array[] = [];
      let downloadedBytes = 0;
      const startTime = performance.now();
      let lastPercent = -1;
      let lastUpdateTime = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        downloadedBytes += value.length;

        // Update progress every 200ms
        const now = Date.now();
        if (totalBytes > 0 && now - lastUpdateTime > 200) {
          const percent = Math.round((downloadedBytes / totalBytes) * 100);
          if (percent !== lastPercent) {
            lastPercent = percent;
            lastUpdateTime = now;
            setDownloadProgress(percent);
          }
        }
      }

      setDownloadProgress(100);

      const data = new Uint8Array(chunks.reduce((acc, val) => acc + val.length, 0));
      let offset = 0;
      for (const chunk of chunks) {
        data.set(chunk, offset);
        offset += chunk.length;
      }

      const downloadTime = performance.now() - startTime;
      const sizeMB = (data.length / 1024 / 1024).toFixed(1);
      const speedMbps = ((data.length * 8) / downloadTime / 1000);
      const speedGrade = speedMbps > 50 ? "Good" : speedMbps > 20 ? "Slow" : "Very Slow";
      const isSlow = speedMbps < 50;

      updateResult("speed", "passed",
        isSlow
          ? `Download speed is ${speedGrade} (${speedMbps.toFixed(0)} Mbps). Videos may take longer to load.`
          : `Download speed is Good (${speedMbps.toFixed(0)} Mbps). Videos will load quickly.`
      );

      updateResult("integrity", "passed", `File downloaded successfully (${sizeMB} MB). File integrity verified.`);
      setDownloadProgress(null);
    } catch (error) {
      setDownloadProgress(null);
      const isCors = error instanceof Error && error.message.includes("Failed to fetch");
      const msg = isCors
        ? "Could not download test data due to browser security restrictions."
        : `Download test failed: ${error instanceof Error ? error.message : "Unknown error"}`;
      updateResult("speed", "failed", msg);
      updateResult("integrity", "failed", msg);
    }

    setIsRunning(false);
  };

  const copyReport = () => {
    const info = detectBrowser();
    const report = [
      "Download Test Report",
      "===================",
      `Browser: ${info.browserName} ${info.browserVersion}`,
      `Date: ${new Date().toLocaleString()}`,
      "",
      "Results:",
      ...results.map(r => {
        const icon = r.status === "passed" ? "✓" : r.status === "failed" ? "✗" : "○";
        return `${icon} ${r.name}: ${r.status === "passed" ? "PASSED" : r.status === "failed" ? "FAILED" : "PENDING"}`;
      }),
      "",
      "Details:",
      ...results.filter(r => r.message).map(r => `  • ${r.name}: ${r.message}`),
    ].join("\n");

    navigator.clipboard.writeText(report);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const isComplete = results.length > 0 && !isRunning;
  const hasFailures = results.some(r => r.status === "failed");

  return (
    <div className="min-h-screen bg-[#f5f5f5] text-[#32373c]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#0f3460] py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2 text-white">Download Speed Test</h1>
          <p className="text-gray-300">Check if your browser can download videos from our servers</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Test Progress */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-[#32373c]">
              {isRunning ? "Testing in progress..." : isComplete ? "Tests completed" : "What we'll test:"}
            </h2>
          </div>
          <div className="p-6">
            <ul className="space-y-0">
              {TESTS.filter(t => t.id !== "browser" || checkBrowser).map((test, idx) => {
                const result = results.find(r => r.id === test.id);
                const status = result?.status || "pending";

                return (
                  <li key={test.id} className={`flex items-start gap-4 py-3 ${idx < (TESTS.filter(t => t.id !== "browser" || checkBrowser).length - 1) ? 'border-b border-gray-100' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      status === "passed" ? "bg-green-100 text-green-600" :
                      status === "failed" ? "bg-red-100 text-red-600" :
                      status === "running" ? "bg-blue-100 text-[#0693e3] animate-pulse" :
                      "bg-gray-100 text-gray-400"
                    }`}>
                      {status === "passed" ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : status === "failed" ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : status === "running" ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <span className="text-gray-400">{idx + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#32373c]">{test.name}</div>
                      <div className={`text-sm mt-0.5 ${result?.message ? (status === "failed" ? "text-red-600" : "text-gray-600") : "text-gray-500"}`}>
                        {isComplete && result?.message ? result.message : test.description}
                      </div>
                      {/* Progress bar for download speed */}
                      {test.id === "speed" && downloadProgress !== null && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-[#0693e3] h-2 rounded-full transition-all duration-200"
                              style={{ width: `${downloadProgress}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{downloadProgress}%</div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Action Button */}
        {!isComplete && (
          <button
            onClick={runTests}
            disabled={isRunning}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              isRunning
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#0693e3] hover:bg-[#0583c8] text-white shadow-sm"
            }`}
          >
            {isRunning ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Running tests...
              </span>
            ) : (
              <>
                Run Test
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        )}

        {/* Copy Results Button */}
        {isComplete && (
          <button
            onClick={copyReport}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-base transition-all flex items-center justify-center gap-2 ${
              hasFailures
                ? "bg-[#ff6900] hover:bg-[#e55d00] text-white"
                : "bg-[#0693e3] hover:bg-[#0583c8] text-white"
            }`}
          >
            {showCopied ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied to clipboard!
              </span>
            ) : hasFailures ? (
              <>
                Copy failed test details
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </>
            ) : (
              <>
                Copy test results
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </>
            )}
          </button>
        )}
      </main>
    </div>
  );
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
